import React from 'react';
import SmartAmountInput from '../../../shared/components/SmartAmountInput';
import { AccountPeriodCloseItemRecord, CustomerOption, NewCloseData } from './types';
import { formatNumber } from './utils';
import { CloseContextSummary } from './CloseContextSummary';

interface Step5ResultProps {
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  accountPeriodCloseResult: any;
  accountPeriodCloseItems: AccountPeriodCloseItemRecord[];
  costOfGoodsSold: number | null;
  setCostOfGoodsSold: (value: number | null) => void;
  batch4Amount: number | null;
  loading: boolean;
  handleCalculate: () => void;
  onBack?: () => void;
  onContinue?: () => void;
}

const Step5Result: React.FC<Step5ResultProps> = ({
  customers,
  newCloseData,
  accountPeriodCloseResult,
  accountPeriodCloseItems,
  costOfGoodsSold,
  setCostOfGoodsSold,
  batch4Amount,
  loading,
  handleCalculate,
  onBack,
  onContinue,
}) => {
  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-end mb-4">
        <div className="flex gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Буцах
            </button>
          )}
          {onContinue && (
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={loading}
            >
              Үргэлжлүүлэх
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <CloseContextSummary customers={customers} newCloseData={newCloseData} />

        {/* Борлуулсан бүтээгдэхүүний өртөг Input */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Борлуулсан бүтээгдэхүүний өртөг:
          </label>
          <div className="flex items-center">
            <SmartAmountInput
              value={costOfGoodsSold}
              onChange={(parsed) => {
                setCostOfGoodsSold(parsed);
              }}
              placeholder="Дүн оруулах"
              className="w-56 px-3 py-2 border border-gray-300 rounded-l-md rounded-r-none shadow-sm text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleCalculate}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-r-md rounded-l-none hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed border-l-0"
            >
              {loading ? 'Тооцоолж байна...' : 'Тооцоолох'}
            </button>
          </div>
        </div>
      </div>

      {accountPeriodCloseItems.length > 0 && (() => {
        // Group items by BatchNumber
        const groupedByBatch = accountPeriodCloseItems.reduce((acc, item) => {
          const batch = item.BatchNumber || 0;
          if (!acc[batch]) {
            acc[batch] = [];
          }
          acc[batch].push(item);
          return acc;
        }, {} as Record<number, AccountPeriodCloseItemRecord[]>);

        // Sort items within each batch by AccountCode
        Object.keys(groupedByBatch).forEach(batchKey => {
          const batch = Number(batchKey);
          groupedByBatch[batch].sort((a, b) => {
            const codeA = a.AccountCode || '';
            const codeB = b.AccountCode || '';
            return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
          });
        });

        // Sort batches
        const sortedBatches = Object.keys(groupedByBatch)
          .map(Number)
          .sort((a, b) => a - b);

        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Дугаар</th>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Дансны код</th>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Дансны нэр</th>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Тайлбар</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">TB Дебет C2</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">TB Кредит C2</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">WT Дебет</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">WT Кредит</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">WT Дебет C2</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">WT Кредит C2</th>
                  <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">WT Дүн</th>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Batch</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBatches.map((batchNumber) => {
                  const batchItems = groupedByBatch[batchNumber];
                  const batchTBDebitTotal = batchItems.reduce((sum, row) => sum + (row.TB_DebitAmountC2 || 0), 0);
                  const batchTBCreditTotal = batchItems.reduce((sum, row) => sum + (row.TB_CreditAmountC2 || 0), 0);
                  const batchWTDebitTotal = batchItems.reduce((sum, row) => sum + ((row.WT_DebitAmount ?? row.DebitAmount) || 0), 0);
                  const batchWTCreditTotal = batchItems.reduce((sum, row) => sum + ((row.WT_CreditAmount ?? row.CreditAmount) || 0), 0);
                  const batchWTDebitC2Total = batchItems.reduce((sum, row) => sum + ((row.WT_DebitAmountC2 ?? row.DebitAmountC2) || 0), 0);
                  const batchWTCreditC2Total = batchItems.reduce((sum, row) => sum + ((row.WT_CreditAmountC2 ?? row.CreditAmountC2) || 0), 0);
                  const batchWTAmountTotal = batchItems.reduce((sum, row) => sum + ((row.WT_Amount ?? row.Amount) || 0), 0);

                  return (
                    <React.Fragment key={batchNumber}>
                      {/* Batch Header */}
                      <tr className="bg-blue-50 border-t border-blue-200">
                        <td colSpan={12} className="px-1 py-1 font-semibold text-blue-800 text-xs border border-gray-300">
                          Batch {batchNumber}
                        </td>
                      </tr>
                      {/* Batch Items */}
                      {batchItems.map((row, index) => {
                        let displayDebit = row.DebitAmountC2;
                        let displayCredit = row.CreditAmountC2;
                        let displayAmount = row.Amount;
                        
                        if (batchNumber === 4 && batch4Amount !== null) {
                          if (index === 0 && row.CreditAmountC2) {
                            displayCredit = batch4Amount;
                            displayAmount = batch4Amount;
                          }
                          if (index === 1 && row.DebitAmountC2) {
                            displayDebit = batch4Amount;
                            displayAmount = batch4Amount;
                          }
                        }
                        
                        const wtDebitAmount = row.WT_DebitAmount ?? row.DebitAmount;
                        const wtCreditAmount = row.WT_CreditAmount ?? row.CreditAmount;
                        const wtDebitAmountC2 = row.WT_DebitAmountC2 ?? displayDebit;
                        const wtCreditAmountC2 = row.WT_CreditAmountC2 ?? displayCredit;
                        const wtAmount = row.WT_Amount ?? displayAmount;
                        
                        return (
                          <tr key={`${batchNumber}-${index}`} className="hover:bg-gray-50">
                            <td className="px-1 py-1 whitespace-nowrap text-gray-900 text-xs border border-gray-300">{row.Number}</td>
                            <td className="px-1 py-1 text-gray-600 text-xs border border-gray-300">{(row.AccountCode && String(row.AccountCode).trim()) ? String(row.AccountCode) : '-'}</td>
                            <td className="px-1 py-1 text-gray-600 text-xs border border-gray-300">{(row.AccountName && String(row.AccountName).trim()) ? String(row.AccountName) : '-'}</td>
                            <td className="px-1 py-1 text-gray-900 text-xs border border-gray-300">{row.Description || '-'}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(row.TB_DebitAmountC2)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(row.TB_CreditAmountC2)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(wtDebitAmount)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(wtCreditAmount)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(wtDebitAmountC2)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(wtCreditAmountC2)}</td>
                            <td className="px-1 py-1 text-right text-gray-900 text-xs border border-gray-300">{formatNumber(wtAmount)}</td>
                            <td className="px-1 py-1 text-gray-600 text-xs border border-gray-300">{row.BatchNumber || '-'}</td>
                          </tr>
                        );
                      })}
                      {/* Batch Subtotal */}
                      <tr className="bg-gray-50 border-b border-gray-300">
                        <td colSpan={4} className="px-1 py-1 font-medium text-gray-700 text-xs border border-gray-300">Batch {batchNumber} - Дэд нийт:</td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(batchTBDebitTotal)}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(batchTBCreditTotal)}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(batchWTDebitTotal)}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(batchWTCreditTotal)}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(
                            batchNumber === 4 && batch4Amount !== null
                              ? batch4Amount
                              : batchWTDebitC2Total
                          )}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(
                            batchNumber === 4 && batch4Amount !== null
                              ? batch4Amount
                              : batchWTCreditC2Total
                          )}
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-gray-700 text-xs border border-gray-300">
                          {formatNumber(
                            batchNumber === 4 && batch4Amount !== null
                              ? batch4Amount * 2
                              : batchWTAmountTotal
                          )}
                        </td>
                        <td className="px-1 py-1 border border-gray-300"></td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-100 sticky bottom-0 z-10">
                {(() => {
                  const batch4OriginalDebit = groupedByBatch[4]?.reduce((sum, row) => sum + ((row.WT_DebitAmountC2 ?? row.DebitAmountC2) || 0), 0) || 0;
                  const batch4OriginalCredit = groupedByBatch[4]?.reduce((sum, row) => sum + ((row.WT_CreditAmountC2 ?? row.CreditAmountC2) || 0), 0) || 0;
                  const batch4OriginalAmount = groupedByBatch[4]?.reduce((sum, row) => sum + ((row.WT_Amount ?? row.Amount) || 0), 0) || 0;
                  
                  const totalTBDebit = accountPeriodCloseItems.reduce((sum, row) => sum + (row.TB_DebitAmountC2 || 0), 0);
                  const totalTBCredit = accountPeriodCloseItems.reduce((sum, row) => sum + (row.TB_CreditAmountC2 || 0), 0);
                  const totalWTDebit = accountPeriodCloseItems.reduce((sum, row) => sum + ((row.WT_DebitAmount ?? row.DebitAmount) || 0), 0);
                  const totalWTCredit = accountPeriodCloseItems.reduce((sum, row) => sum + ((row.WT_CreditAmount ?? row.CreditAmount) || 0), 0);
                  const totalWTDebitC2 = accountPeriodCloseItems.reduce((sum, row) => sum + ((row.WT_DebitAmountC2 ?? row.DebitAmountC2) || 0), 0);
                  const totalWTCreditC2 = accountPeriodCloseItems.reduce((sum, row) => sum + ((row.WT_CreditAmountC2 ?? row.CreditAmountC2) || 0), 0);
                  const totalWTAmount = accountPeriodCloseItems.reduce((sum, row) => sum + ((row.WT_Amount ?? row.Amount) || 0), 0);
                  
                  const finalWTDebitC2 = batch4Amount !== null && groupedByBatch[4] 
                    ? totalWTDebitC2 - batch4OriginalDebit + batch4Amount 
                    : totalWTDebitC2;
                  const finalWTCreditC2 = batch4Amount !== null && groupedByBatch[4] 
                    ? totalWTCreditC2 - batch4OriginalCredit + batch4Amount 
                    : totalWTCreditC2;
                  const finalWTAmount = batch4Amount !== null && groupedByBatch[4] 
                    ? totalWTAmount - batch4OriginalAmount + (batch4Amount * 2) 
                    : totalWTAmount;
                  
                  return (
                    <tr>
                      <td colSpan={4} className="px-1 py-1 font-semibold text-gray-900 text-xs border border-gray-300">Нийт:</td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(totalTBDebit)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(totalTBCredit)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(totalWTDebit)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(totalWTCredit)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(finalWTDebitC2)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(finalWTCreditC2)}
                      </td>
                      <td className="px-1 py-1 text-right font-semibold text-gray-900 text-xs border border-gray-300">
                        {formatNumber(finalWTAmount)}
                      </td>
                      <td className="px-1 py-1 border border-gray-300"></td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        );
      })()}
    </>
  );
};

export default Step5Result;
